import React, {useCallback, useContext, useEffect, useMemo} from 'react';

import {DateRangeInput, FormInput, NumberInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {CONTRACT_ID_EXPLANATION} from 'src/constants';
import {CustomValidatedControl, ValidatedControl} from 'src/contexts/validation-context';
import {useConferenceAccountCheck} from 'src/hooks/useConferenceAccountCheck';
import {SEATS_RANGE_LICENSES} from 'src/modules/profile/account/constants';
import {LicenseType as LicenseTypes} from 'src/openapi/admin/models/LicenseType';
import {SEATS_RANGE_OPTIONS, getSeatsRangeValue, isLicenseTypeProhibitedForCreation} from 'src/utils/license-util';

import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import FormSection from '@pb/kit-component-form-section';
import {Integer} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';
import {LocaleContext} from '@pb/kit-context-locale';

import {LicenseSkeleton} from './license-skeleton';

import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';
import type {Error} from 'src/openapi/admin/models/Error';
import type {LicenseUpdateRequest} from 'src/openapi/admin/models/LicenseUpdateRequest';

type Props = {
    licenseLoading: boolean,
    loadingError: Error | null,
    license: LicenseUpdateRequest,
    supportedLicenseTypes: Array<DictionaryItem>,
    onChangeLicense: React.Dispatch<React.SetStateAction<LicenseUpdateRequest>>,
    disableLicenseTypeSelect?: boolean,
    onFieldChange: (parameterName: string) => void,
};

export const LicenseSection = (props: Props) => {
    const {
        licenseLoading,
        loadingError,
        license,
        supportedLicenseTypes,
        onChangeLicense,
        disableLicenseTypeSelect,
        onFieldChange,
    } = props;

    const {licenseType, startDate, endDate} = license;
    const isConferenceAccount = useConferenceAccountCheck();
    const {locale} = useContext(LocaleContext);

    const onChangeStartDate = useCallback((date: string) => {
        onFieldChange?.('License Start Date');
        onChangeLicense(prevState => ({...prevState, startDate: date}));
    }, [onChangeLicense, onFieldChange]);

    const onChangeEndDate = useCallback((date: string) => {
        onFieldChange?.('License End Date');
        onChangeLicense(prevState => ({...prevState, endDate: date}));
    }, [onChangeLicense, onFieldChange]);

    const onChangeSeats = useCallback((concurrentUserCount?: number) => {
        onFieldChange?.('License Seats');
        onChangeLicense(prevState => ({...prevState, concurrentUserCount}));
    }, [onChangeLicense, onFieldChange]);

    const onChangeLicenseType = useCallback((type: LicenseTypes) => {
        onFieldChange?.('License Type');
        onChangeLicense(prevState => {
            const isSwitchingFromSeatsRange =
                SEATS_RANGE_LICENSES.includes(prevState.licenseType) && !SEATS_RANGE_LICENSES.includes(type);
            const shouldResetSeats = type === LicenseTypes.CU || isSwitchingFromSeatsRange;

            return {
                ...prevState,
                licenseType: type,
                concurrentUserCount: shouldResetSeats ? undefined : prevState.concurrentUserCount,
            };
        });
    }, [onChangeLicense, onFieldChange]);

    const onChangeContractID = useCallback((contractId: string) => {
        onFieldChange?.('Contract ID');
        onChangeLicense(prevState => ({
            ...prevState,
            contractId: contractId || undefined,
        }));
    }, [onChangeLicense, onFieldChange]);

    const licenseTypeDictionary = useMemo(() => {
        if (disableLicenseTypeSelect) {
            return supportedLicenseTypes;
        }

        return supportedLicenseTypes.filter(item => (
            !isLicenseTypeProhibitedForCreation(item.code as LicenseTypes)
        ));
    }, [supportedLicenseTypes, disableLicenseTypeSelect]);

    const licenseTypeOptions = useMemo(() => {
        return licenseTypeDictionary.map(item => (
            <ComboboxItem key={item.code} value={item.code} caption={item.description}/>
        ));
    }, [licenseTypeDictionary]);

    const limitedSeatsLicenses = [LicenseTypes.CU, LicenseTypes.SB, LicenseTypes.US, LicenseTypes.GP, LicenseTypes.UGP];

    useEffect(() => {
        if (SEATS_RANGE_LICENSES.includes(license.licenseType) && !license.concurrentUserCount) {
            onChangeSeats(100);
        }
    }, [onChangeSeats, license.concurrentUserCount, license.licenseType]);

    return (
        <Skeleton view={<LicenseSkeleton/>} shown={licenseLoading}>
            <ServerResponseHandler
                serverError={loadingError}
                smallView={true}>
                <FormSection headline="License">
                    <ResponsiveElementGroup>
                        <div>
                            <Label><RequiredMark label="License Period"/></Label>
                            <Group direction="v" gap="xs" alignItems="start">
                                <ValidatedControl name="license-start-date">
                                    {(invalid: boolean) => (
                                        <DateRangeInput
                                            id="license-start-date-picker"
                                            width={300}
                                            label="Starts"
                                            invalid={invalid}
                                            clearable={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            selectRangeType="start"
                                            onChange={onChangeStartDate}
                                        />
                                    )}
                                </ValidatedControl>
                                <ValidatedControl name="license-end-date">
                                    {(invalid: boolean) => (
                                        <DateRangeInput
                                            id="license-end-date-picker"
                                            width={300}
                                            label={<>Ends&nbsp;&nbsp;</>}
                                            invalid={invalid}
                                            clearable={true}
                                            preventPastDate={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            selectRangeType="end"
                                            onChange={onChangeEndDate}
                                        />
                                    )}
                                </ValidatedControl>
                            </Group>
                        </div>
                        <WidthItem width={300}>
                            <Label id="license-type-label" htmlFor="license-type-combobox">
                                License Type
                            </Label>
                            <ComboBox
                                id="license-type-combobox"
                                disabled={disableLicenseTypeSelect || isConferenceAccount}
                                value={licenseType}
                                aria-labelledby="license-type-label"
                                onChange={onChangeLicenseType}>
                                {licenseTypeOptions}
                            </ComboBox>
                        </WidthItem>
                        {limitedSeatsLicenses.includes(licenseType) && (
                            SEATS_RANGE_LICENSES.includes(license.licenseType) ? (
                                <WidthItem width={300}>
                                    <Label id="seats-range-label" htmlFor="seats-range-combobox">
                                        Seat Range
                                    </Label>
                                    <ComboBox
                                        id="seats-range-combobox"
                                        value={getSeatsRangeValue(license.concurrentUserCount) || undefined}
                                        aria-labelledby="seats-range-label"
                                        onChange={onChangeSeats}
                                        placeholder="Select Seat Range">
                                        {SEATS_RANGE_OPTIONS.filter(option => option.value !== null)
                                            .map(({value, caption}) => (
                                                <ComboboxItem key={String(value)} value={value} caption={caption}/>
                                            ))}
                                    </ComboBox>
                                </WidthItem>
                            ) : (
                                <CustomValidatedControl name="license-seats">
                                    {error => (
                                        <NumberInput
                                            width={300}
                                            label={(
                                                <span>
                                                    Seats{licenseType === LicenseTypes.CU ? '*' : ''}
                                                </span>
                                            )}
                                            onBlur={onChangeSeats}
                                            value={license.concurrentUserCount ?? undefined}
                                            maxValue={9999}
                                            valid={error ? false : undefined}
                                            description={error}
                                            displayedValue={typeof license.concurrentUserCount === 'number'
                                                ? Integer.format(license.concurrentUserCount, locale)
                                                : ''}
                                        />
                                    )}
                                </CustomValidatedControl>
                            )
                        )}
                        <FormInput
                            width={300}
                            label={
                                <Info type={{popover: CONTRACT_ID_EXPLANATION}}>Contract ID</Info>
                            }
                            value={license.contractId || ''}
                            maxLength={255}
                            onBlur={onChangeContractID}
                        />
                    </ResponsiveElementGroup>
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
